import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import DealerPlanTierCard, {
  planRank,
  UPGRADE_PLAN_ORDER,
  UsageMeter,
} from "../../components/dealer/DealerPlanTierCard";
import DealerPlanUpgradeModal from "../../components/dealer/DealerPlanUpgradeModal";
import { planBadgeClass, trackDealerPlanEvent } from "../../constants/automapDealerPlanConstants";
import { automapDealerPortalPath } from "../../constants/automapDealerPortalRoutes";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useLocale } from "../../intl";
import { fetchDealerPlanPortal } from "../../services/automapDealerPlanApi";
import {
  fetchDealerFeaturedPortal,
  requestFeaturedPlacement,
} from "../../services/automapFeaturedPlacementApi";
import { trackFeaturedPlacementEvent } from "../../constants/automapFeaturedPlacementConstants";

export default function DealerPlanPage() {
  const { dealerId } = useOutletContext();
  const { locale } = useLocale();
  const {
    t,
    translateError,
    formatPlanLabel,
    formatPlanLimit,
    formatReportsAccess,
    formatPlanTrialStatus,
    formatPlanConversionStage,
    localizePlan,
    localizeFeatured,
  } = useDealerPortalIntl();
  const [rawPlan, setRawPlan] = useState(null);
  const [rawFeatured, setRawFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [requestStatus, setRequestStatus] = useState("");
  const [upgradeModalPlanKey, setUpgradeModalPlanKey] = useState("");
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [pendingUpgradeRequest, setPendingUpgradeRequest] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [planResult, featuredResult] = await Promise.allSettled([
        fetchDealerPlanPortal(dealerId),
        fetchDealerFeaturedPortal(dealerId),
      ]);
      if (planResult.status === "fulfilled") {
        setRawPlan(planResult.value.plan);
        setPendingUpgradeRequest(planResult.value.plan?.pendingUpgradeRequest || null);
      } else {
        throw planResult.reason;
      }
      if (featuredResult.status === "fulfilled") {
        setRawFeatured(featuredResult.value.featured);
        trackFeaturedPlacementEvent("dealer_featured_performance_viewed", { dealerId });
      } else {
        setRawFeatured(null);
      }
    } catch (err) {
      setError(translateError(err?.message, "loadPlan"));
      setRawPlan(null);
      setPendingUpgradeRequest(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    trackDealerPlanEvent("dealer_plan_page_viewed", { dealerId });
    load();
  }, [dealerId]);

  const data = useMemo(() => (rawPlan ? localizePlan(rawPlan) : null), [rawPlan, localizePlan]);
  const featured = useMemo(
    () => (rawFeatured ? localizeFeatured(rawFeatured) : null),
    [rawFeatured, localizeFeatured]
  );

  const openUpgradeModal = (planKey = "") => {
    trackDealerPlanEvent("dealer_plan_upgrade_clicked", { dealerId, planKey: planKey || null });
    setUpgradeModalPlanKey(planKey || "");
    setUpgradeModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setUpgradeModalOpen(false);
    setUpgradeModalPlanKey("");
  };

  const handleUpgradeSubmitted = (request) => {
    setPendingUpgradeRequest(request);
    trackDealerPlanEvent("dealer_plan_upgrade_submitted", {
      dealerId,
      targetPlanKey: request?.targetPlanKey,
    });
  };

  const submitFeaturedRequest = async () => {
    setRequestStatus("");
    try {
      await requestFeaturedPlacement(dealerId, {
        requestedPlacementType: "featured_listing",
        message: requestMessage || t("plan.featuredDefaultRequest"),
      });
      trackFeaturedPlacementEvent("dealer_featured_request_created", { dealerId });
      setRequestStatus(t("plan.requestSent"));
      setRequestMessage("");
    } catch (err) {
      setRequestStatus(translateError(err.message, "generic"));
    }
  };

  const comparisonPlans = useMemo(() => {
    const plans = data?.comparisonPlans || [];
    return UPGRADE_PLAN_ORDER.map((key) => plans.find((p) => p.planKey === key)).filter(Boolean);
  }, [data?.comparisonPlans]);

  if (loading) {
    return (
      <div className="dealer-plan-page">
        <p className="dealer-state-box">{t("plan.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dealer-plan-page">
        <p className="dealer-state-box">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const plan = data.plan || {};
  const usage = data.usage || {};
  const warnings = data.warnings || [];
  const featureAccess = data.featureAccess || {};
  const trial = data.trial;
  const currentRank = planRank(plan.planKey);
  const currentPlanCard = comparisonPlans.find((p) => p.planKey === plan.planKey) || {
    ...plan,
    planKey: plan.planKey,
    publicName: plan.publicName,
    description: plan.description,
  };

  return (
    <div className="dealer-plan-page">
      <header className="dealer-plan-page__hero">
        <div>
          <p className="dealer-plan-page__eyebrow">{t("plan.pageEyebrow")}</p>
          <h1>{t("plan.title")}</h1>
          <p className="dealer-plan-page__subtitle">{t("plan.subtitleProfessional")}</p>
        </div>
        <div className="dealer-plan-page__hero-actions">
          <button type="button" className="dealer-portal-btn primary" onClick={() => openUpgradeModal()}>
            {t("plan.upgradeCta")}
          </button>
          <Link className="dealer-portal-btn" to={automapDealerPortalPath("reportes")}>
            {t("plan.viewReports")}
          </Link>
        </div>
      </header>

      {pendingUpgradeRequest ? (
        <section className="dealer-plan-page__pending-upgrade" aria-live="polite">
          <span aria-hidden="true">⏳</span>
          <p>
            {t("plan.upgradePendingBanner", {
              plan: formatPlanLabel(pendingUpgradeRequest.targetPlanKey),
              date: new Date(pendingUpgradeRequest.createdAt).toLocaleDateString(locale),
            })}
          </p>
        </section>
      ) : null}

      {warnings.length > 0 ? (
        <section className="dealer-plan-page__warnings" aria-label={t("plan.warningsTitle")}>
          {warnings.map((w) => (
            <div
              key={w.type}
              className={`dealer-plan-warning${w.severity === "critical" ? " dealer-plan-warning--critical" : ""}`}
            >
              <span aria-hidden="true">⚠️</span>
              <p>{w.message}</p>
            </div>
          ))}
        </section>
      ) : null}

      <section className="dealer-plan-page__current" aria-labelledby="dealer-plan-current-heading">
        <div className="dealer-plan-page__section-head">
          <h2 id="dealer-plan-current-heading">{t("plan.currentPlanTitle")}</h2>
          <span className={`admin-badge ${planBadgeClass(plan.planKey)}`}>
            {formatPlanLabel(plan.planKey, plan.publicName)}
          </span>
        </div>

        <div className="dealer-plan-page__current-grid">
          <DealerPlanTierCard plan={currentPlanCard} isCurrent />

          <div className="dealer-plan-page__usage-panel">
            <h3>{t("plan.usageTitle")}</h3>
            {trial?.trialStatus && trial.trialStatus !== "not_started" ? (
              <div className="dealer-plan-trial-banner">
                <span aria-hidden="true">🎁</span>
                <div>
                  <strong>{t("plan.trialLabel")}</strong>
                  <p>
                    {formatPlanTrialStatus(trial.trialStatus)} · {t("plan.trialStage")}:{" "}
                    {formatPlanConversionStage(trial.conversionStage)}
                  </p>
                </div>
              </div>
            ) : null}

            <UsageMeter
              emoji="🚗"
              label={t("plan.activeListings")}
              used={usage.activeListings}
              limit={usage.listingLimit}
              formatPlanLimit={formatPlanLimit}
            />
            <UsageMeter
              emoji="📩"
              label={t("plan.leadsThisMonth")}
              used={usage.leadsThisMonth}
              limit={usage.leadLimitMonthly}
              formatPlanLimit={formatPlanLimit}
            />

            <dl className="dealer-plan-included">
              <div>
                <dt>{t("plan.reportsAccess")}</dt>
                <dd>{formatReportsAccess(featureAccess.performanceReports)}</dd>
              </div>
              <div>
                <dt>{t("plan.featuredEligible")}</dt>
                <dd>
                  {featureAccess.featuredPlacement ? t("plan.featuredEligibleYes") : t("plan.notIncluded")}
                </dd>
              </div>
              <div>
                <dt>{t("plan.featureBulkUpload")}</dt>
                <dd>{featureAccess.bulkUpload ? t("plan.featureYes") : t("plan.featureNo")}</dd>
              </div>
            </dl>

            <div className="dealer-plan-page__quick-links">
              <Link className="dealer-portal-btn" to={automapDealerPortalPath("inventario")}>
                {t("plan.updateInventory")}
              </Link>
              <Link className="dealer-portal-btn" to={automapDealerPortalPath("leads")}>
                {t("plan.openLeads")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="dealer-plan-page__panel" aria-labelledby="dealer-plan-featured-heading">
        <div className="dealer-plan-page__section-head">
          <h2 id="dealer-plan-featured-heading">{t("plan.featuredSectionTitle")}</h2>
        </div>
        {featured?.featuredEligible ? (
          <div className="dealer-plan-featured">
            <p className="dealer-plan-featured__intro">{t("plan.featuredIntro")}</p>
            <div className="dealer-plan-featured__stats">
              <span>
                {t("plan.featuredActive")}: {featured.activePlacements?.length ?? 0}
              </span>
              <span>
                {t("plan.featuredScheduled")}: {featured.scheduledPlacements?.length ?? 0}
              </span>
              <span>
                {t("reports.impressions")}: {featured.performance?.impressions ?? 0}
              </span>
              <span>
                {t("reports.leadsReceived")}: {featured.performance?.leads ?? 0}
              </span>
            </div>
            <label className="dealer-plan-featured__request">
              <span>{t("plan.featuredRequestLabel")}</span>
              <textarea
                placeholder={t("plan.featuredRequestPlaceholder")}
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
              />
            </label>
            <button type="button" className="dealer-portal-btn primary" onClick={submitFeaturedRequest}>
              {t("plan.requestFeatured")}
            </button>
            {requestStatus ? <p className="muted">{requestStatus}</p> : null}
          </div>
        ) : (
          <div className="dealer-plan-locked-inline">
            <span aria-hidden="true">🔒</span>
            <div>
              <strong>{t("plan.featuredUpgrade")}</strong>
              <p>{featured?.upgradeMessage || t("plan.featuredUpgradeHint")}</p>
              <button type="button" className="dealer-portal-btn primary" onClick={() => openUpgradeModal("growth")}>
                {t("plan.featuredUpgradeCta")}
              </button>
            </div>
          </div>
        )}
      </section>

      {(!featureAccess.bulkUpload || !featureAccess.performanceReports) ? (
        <section className="dealer-plan-page__panel" aria-labelledby="dealer-plan-locked-heading">
          <h2 id="dealer-plan-locked-heading">{t("plan.lockedFeaturesTitle")}</h2>
          <div className="dealer-plan-page__locked-grid">
            {!featureAccess.bulkUpload ? (
              <div className="dealer-plan-locked-inline">
                <span aria-hidden="true">📤</span>
                <div>
                  <strong>{t("plan.lockedBulkTitle")}</strong>
                  <p>{t("plan.lockedBulkDesc")}</p>
                </div>
              </div>
            ) : null}
            {!featureAccess.performanceReports ? (
              <div className="dealer-plan-locked-inline">
                <span aria-hidden="true">📊</span>
                <div>
                  <strong>{t("plan.lockedReportsTitle")}</strong>
                  <p>{t("plan.lockedReportsDesc")}</p>
                </div>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="dealer-plan-page__panel" aria-labelledby="dealer-plan-compare-heading">
        <div className="dealer-plan-page__section-head">
          <h2 id="dealer-plan-compare-heading">{t("plan.comparisonTitle")}</h2>
          <p className="muted">{t("plan.comparisonPricingNote")}</p>
        </div>
        <div className="dealer-plan-page__tier-grid dealer-plan-page__tier-grid--compare">
          {comparisonPlans.map((tier) => (
            <DealerPlanTierCard
              key={tier.planKey}
              plan={tier}
              isCurrent={tier.planKey === plan.planKey}
              isRecommended={tier.planKey === "growth" && tier.planKey !== plan.planKey}
              onUpgrade={
                tier.planKey !== plan.planKey && planRank(tier.planKey) > currentRank
                  ? () => openUpgradeModal(tier.planKey)
                  : null
              }
              upgradeLabel={t("plan.upgradeToPlan", {
                plan: formatPlanLabel(tier.planKey, tier.publicName),
              })}
            />
          ))}
        </div>
      </section>

      <section className="dealer-plan-page__panel dealer-plan-page__faq">
        <h2>{t("plan.faqTitle")}</h2>
        <div className="dealer-plan-faq">
          <article>
            <h3>{t("plan.faqBillingQuestion")}</h3>
            <p>{t("plan.faqBillingAnswer")}</p>
          </article>
          <article>
            <h3>{t("plan.faqUpgradeQuestion")}</h3>
            <p>{t("plan.faqUpgradeAnswer")}</p>
          </article>
          <article>
            <h3>{t("plan.faqLimitsQuestion")}</h3>
            <p>{t("plan.faqLimitsAnswer")}</p>
          </article>
        </div>
        <p className="dealer-plan-page__billing-note">
          <span aria-hidden="true">💳</span> {data.billingPlaceholder?.message || t("plan.billingNote")}
        </p>
      </section>

      <DealerPlanUpgradeModal
        isOpen={upgradeModalOpen}
        dealerId={dealerId}
        currentPlanKey={plan.planKey}
        comparisonPlans={comparisonPlans}
        initialTargetPlanKey={upgradeModalPlanKey}
        pendingRequest={pendingUpgradeRequest}
        onClose={closeUpgradeModal}
        onSubmitted={handleUpgradeSubmitted}
      />
    </div>
  );
}
